import type { Case, CaseDocument, Client } from '@core/domain/model';
import { full, plural } from '@core/ui/RuDates';

/**
 * Тексты раздела «Клиенты» — порт `ClientsFormatter` и `ClientDetailFormatter`.
 *
 * Форматирования в вёрстке нет: так его можно проверить тестом, а не глазами.
 */

/** Строка списка клиентов — «Структура» §7.1. */
export interface ClientListItem {
  id: string;
  name: string;
  /** «2 активных · всего 5» либо «Дел нет»: главное о клиенте — сколько с ним работы. */
  casesLine: string;
  /** Телефон одной строкой, если он есть: по нему клиента и узнают в списке. */
  phone: string | null;
  /** Первые буквы имени для кружка: фотографий у нас нет и не будет. */
  initials: string;
}

/**
 * «2 активных · всего 5», «Дел нет».
 *
 * Активные названы первыми: в списке юрист ищет того, с кем работа идёт сейчас,
 * а не того, у кого дел было больше всех за всё время.
 */
export function casesLine(active: number, total: number): string {
  if (total === 0) return 'Дел нет';
  if (active === 0) return `Активных нет · всего ${plural(total, 'дело', 'дела', 'дел')}`;
  if (active === total) {
    return plural(active, 'активное дело', 'активных дела', 'активных дел');
  }

  return `${plural(active, 'активное', 'активных', 'активных')} · всего ${total}`;
}

/**
 * «Иванов Иван Иванович» → «ИИ», «ООО «Ромашка»» → «ОР».
 *
 * Две буквы, не больше: кружок маленький, а три буквы в нём при системном шрифте
 * 200 % уже не помещаются.
 */
export function initialsOf(fullName: string): string {
  const words = fullName
    .split(/[\s\-«»"']+/)
    .filter((word) => word.length > 0)
    .filter((word) => /\p{L}/u.test(word[0] ?? ''));

  const initials = words
    .slice(0, 2)
    .map((word) => (word[0] ?? '').toUpperCase())
    .join('');

  return initials || '?';
}

export function toListItem(client: Client, cases: Case[]): ClientListItem {
  const active = cases.filter((item) => item.status === 'Active').length;

  return {
    id: client.id,
    name: client.fullName,
    casesLine: casesLine(active, cases.length),
    phone: client.phone?.trim() ? client.phone : null,
    initials: initialsOf(client.fullName),
  };
}

/**
 * «14 августа 1978 · 47 лет».
 *
 * Возраст считаем сами и показываем рядом: юристу он нужен постоянно — от него зависят
 * и дееспособность, и сроки, и льготы, — а в уме от даты его не берут.
 */
export function birthDateLine(birthDate: string, today: number): string {
  const [year, month, day] = birthDate.split('-').map(Number);
  if (!year || !month || !day) return '';

  const born = new Date(year, month - 1, day);
  const at = new Date(today);

  let years = at.getFullYear() - year;
  // День рождения в этом году ещё не наступил — год не засчитан.
  const passed =
    at.getMonth() > born.getMonth() ||
    (at.getMonth() === born.getMonth() && at.getDate() >= born.getDate());
  if (!passed) years -= 1;

  return `${full(born)} · ${plural(years, 'год', 'года', 'лет')}`;
}

/** Дело клиента в его карточке — «Структура» §8.2. */
export interface ClientCaseRow {
  id: string;
  title: string;
  subtitle: string;
  isActive: boolean;
}

/** Документ, привязанный к самому клиенту, а не к делу — «Структура» §8.3. */
export interface ClientDocumentRow {
  id: string;
  title: string;
  meta: string;
}

export const toDocumentRow = (document: CaseDocument): ClientDocumentRow => ({
  id: document.id,
  title: document.title,
  meta: full(new Date(document.addedAt)),
});
